import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from '../../utils/axiosConfig';
import { 
  DocumentArrowDownIcon, 
  TableCellsIcon, 
  MagnifyingGlassIcon,
  XMarkIcon 
} from '@heroicons/react/24/outline';
import { useToast } from '../../hooks/useToast';
import { formatCursoLabel, formatCursoNombreAnio } from '../../utils/cursoLabel';

interface Curso {
  id: number;
  nombre: string;
  codigo: string;
  anio?: number;
  tipoEvaluacion: string;
  cantidadTemas: number;
}

interface FechaInfo {
  fecha: string | null;
  tipo: string | null;
  descripcion: string | null;
  notas: string[];
}

interface AlumnoResumen {
  alumno: {
    id: number;
    nombre: string;
    apellido: string;
    numeroAlumno: string;
  };
  fecha1: FechaInfo;
  fecha2: FechaInfo;
  fecha3: FechaInfo;
  notaFinal: string[];
  condicion: 'APROBADO' | 'DESAPROBADO' | 'AUSENTE';
}

interface ResumenNotas {
  curso: Curso;
  fechas: {
    fecha1: { id: number; fecha: string; tipo: string; descripcion: string | null } | null;
    fecha2: { id: number; fecha: string; tipo: string; descripcion: string | null } | null;
    fecha3: { id: number; fecha: string; tipo: string; descripcion: string | null } | null;
  };
  alumnos: AlumnoResumen[];
  totalAlumnos: number;
  aprobados: number;
  desaprobados: number;
  ausentes: number;
  fechaGeneracion: string;
}

const ResumenNotasPage: React.FC = () => {
  const [selectedCurso, setSelectedCurso] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { showSuccess, showError } = useToast();

  const { data: cursos, isLoading: loadingCursos } = useQuery({
    queryKey: ['cursos'],
    queryFn: async () => {
      try {
        const response = await axios.get(`/cursos`);
        return Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('Error fetching cursos:', error);
        return [];
      }
    },
  });

  const { data: resumen, isLoading: loadingResumen } = useQuery<ResumenNotas>({
    queryKey: ['resumen-notas', selectedCurso],
    queryFn: async () => {
      if (!selectedCurso) return null;
      try {
        const response = await axios.get(`/notas/curso/${selectedCurso}/resumen-notas`);
        return response.data;
      } catch (error) {
        console.error('Error fetching resumen:', error);
        return null;
      }
    },
    enabled: !!selectedCurso,
  });

  const alumnosFiltrados = useMemo(() => {
    if (!resumen?.alumnos) return [];
    if (!searchTerm.trim()) return resumen.alumnos;
    
    const termLower = searchTerm.toLowerCase().trim();
    return resumen.alumnos.filter(a => 
      a.alumno.apellido.toLowerCase().includes(termLower) ||
      a.alumno.nombre.toLowerCase().includes(termLower) ||
      a.alumno.numeroAlumno.toLowerCase().includes(termLower)
    );
  }, [resumen?.alumnos, searchTerm]);

  const formatFecha = (fecha: string | null) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getTipoFechaLabel = (tipo: string | null) => {
    if (!tipo) return '';
    const tipos: { [key: string]: string } = {
      'primera_fecha': '1ra Fecha',
      'recuperatorio_1': 'Recup. 1',
      'recuperatorio_2': 'Recup. 2',
      'fecha_extra': 'Extra',
    };
    return tipos[tipo] || tipo;
  };

  const getCondicionColor = (condicion: string) => {
    switch (condicion) {
      case 'APROBADO': return 'bg-green-100 text-green-800 border-green-200';
      case 'DESAPROBADO': return 'bg-red-100 text-red-800 border-red-200';
      case 'AUSENTE': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getNotaColor = (nota: string) => {
    switch (nota?.toUpperCase()) {
      case 'A': return 'text-green-600 font-bold';
      case 'D': return 'text-red-600 font-bold';
      case 'DU': return 'text-orange-600 font-bold';
      case 'B': return 'text-gray-400';
      case '-': return 'text-gray-400';
      default: return 'text-gray-600';
    }
  };

  const handleExportExcel = () => {
    if (!resumen) return;

    try {
      const cantidadTemas = resumen.curso.cantidadTemas || 3;
      
      const headers = ['#', 'Legajo', 'Apellido', 'Nombre'];
      
      for (let t = 1; t <= cantidadTemas; t++) {
        headers.push(`F1-T${t}`);
      }
      for (let t = 1; t <= cantidadTemas; t++) {
        headers.push(`F2-T${t}`);
      }
      for (let t = 1; t <= cantidadTemas; t++) {
        headers.push(`F3-T${t}`);
      }
      for (let t = 1; t <= cantidadTemas; t++) {
        headers.push(`Final-T${t}`);
      }
      headers.push('Condicion');

      const rows = alumnosFiltrados.map((alumno, index) => {
        const row: string[] = [
          (index + 1).toString(),
          alumno.alumno.numeroAlumno,
          alumno.alumno.apellido,
          alumno.alumno.nombre,
        ];

        for (let t = 0; t < cantidadTemas; t++) {
          row.push(alumno.fecha1.notas[t] || '-');
        }
        for (let t = 0; t < cantidadTemas; t++) {
          row.push(alumno.fecha2.notas[t] || '-');
        }
        for (let t = 0; t < cantidadTemas; t++) {
          row.push(alumno.fecha3.notas[t] || '-');
        }
        for (let t = 0; t < cantidadTemas; t++) {
          row.push(alumno.notaFinal[t] || '-');
        }
        row.push(alumno.condicion);

        return row;
      });

      const csvContent = [
        `Curso: ${formatCursoLabel(resumen.curso)}`,
        `Fecha 1: ${formatFecha(resumen.fechas.fecha1?.fecha || null)} - ${getTipoFechaLabel(resumen.fechas.fecha1?.tipo || null)}`,
        `Fecha 2: ${formatFecha(resumen.fechas.fecha2?.fecha || null)} - ${getTipoFechaLabel(resumen.fechas.fecha2?.tipo || null)}`,
        `Fecha 3: ${formatFecha(resumen.fechas.fecha3?.fecha || null)} - ${getTipoFechaLabel(resumen.fechas.fecha3?.tipo || null)}`,
        '',
        `Total: ${resumen.totalAlumnos} | Aprobados: ${resumen.aprobados} | Desaprobados: ${resumen.desaprobados} | Ausentes: ${resumen.ausentes}`,
        '',
        headers.join('\t'),
        ...rows.map(row => row.join('\t'))
      ].join('\n');

      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `Resumen_Notas_${formatCursoNombreAnio(resumen.curso).replace(/\s+/g, '_')}.xls`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showSuccess('Excel generado', 'El archivo se ha descargado correctamente');
    } catch (error: any) {
      console.error('Error al generar Excel:', error);
      showError('Error', 'No se pudo generar el Excel');
    }
  };

  if (loadingCursos) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando cursos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <TableCellsIcon className="h-8 w-8 mr-3 text-blue-600" />
          Resumen de Notas por Alumno
        </h1>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Seleccionar Curso
          </label>
          <select
            value={selectedCurso || ''}
            onChange={(e) => {
              setSelectedCurso(e.target.value ? parseInt(e.target.value) : null);
              setSearchTerm('');
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Seleccionar curso</option>
            {Array.isArray(cursos) && cursos.map((curso: Curso) => (
              <option key={curso.id} value={curso.id}>
                {formatCursoLabel(curso)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedCurso && (
        <>
          {loadingResumen ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Cargando resumen...</p>
              </div>
            </div>
          ) : resumen ? (
            <>
              {/* Resumen estadístico */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
                  <h2 className="text-xl font-semibold text-gray-900">
                    {formatCursoNombreAnio(resumen.curso)} - Resumen
                  </h2>
                  <button
                    onClick={handleExportExcel}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                    Exportar a Excel
                  </button>
                </div>

                {/* Fechas de examen */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <div className="text-xs font-medium text-blue-600 uppercase">Fecha 1</div>
                    <div className="text-sm font-semibold text-blue-900 mt-1">
                      {formatFecha(resumen.fechas.fecha1?.fecha || null)}
                    </div>
                    <div className="text-xs text-blue-700">
                      {getTipoFechaLabel(resumen.fechas.fecha1?.tipo || null)}
                    </div>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                    <div className="text-xs font-medium text-purple-600 uppercase">Fecha 2</div>
                    <div className="text-sm font-semibold text-purple-900 mt-1">
                      {formatFecha(resumen.fechas.fecha2?.fecha || null)}
                    </div>
                    <div className="text-xs text-purple-700">
                      {getTipoFechaLabel(resumen.fechas.fecha2?.tipo || null)}
                    </div>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                    <div className="text-xs font-medium text-orange-600 uppercase">Fecha 3</div>
                    <div className="text-sm font-semibold text-orange-900 mt-1">
                      {formatFecha(resumen.fechas.fecha3?.fecha || null)}
                    </div>
                    <div className="text-xs text-orange-700">
                      {getTipoFechaLabel(resumen.fechas.fecha3?.tipo || null)}
                    </div>
                  </div>
                </div>

                {/* Estadísticas */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="text-sm font-medium text-gray-600">Total Alumnos</div>
                    <div className="text-2xl font-bold text-gray-900 mt-1">
                      {resumen.totalAlumnos}
                    </div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <div className="text-sm font-medium text-green-600">Aprobados</div>
                    <div className="text-2xl font-bold text-green-900 mt-1">
                      {resumen.aprobados}
                    </div>
                    <div className="text-xs text-green-700">
                      {resumen.totalAlumnos > 0 
                        ? `${((resumen.aprobados / resumen.totalAlumnos) * 100).toFixed(1)}%`
                        : '0%'}
                    </div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                    <div className="text-sm font-medium text-red-600">Desaprobados</div>
                    <div className="text-2xl font-bold text-red-900 mt-1">
                      {resumen.desaprobados}
                    </div>
                    <div className="text-xs text-red-700">
                      {resumen.totalAlumnos > 0 
                        ? `${((resumen.desaprobados / resumen.totalAlumnos) * 100).toFixed(1)}%`
                        : '0%'}
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="text-sm font-medium text-gray-600">Ausentes</div>
                    <div className="text-2xl font-bold text-gray-900 mt-1">
                      {resumen.ausentes}
                    </div>
                    <div className="text-xs text-gray-700">
                      {resumen.totalAlumnos > 0 
                        ? `${((resumen.ausentes / resumen.totalAlumnos) * 100).toFixed(1)}%`
                        : '0%'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Buscador */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar por legajo, apellido o nombre..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
                {searchTerm && (
                  <div className="mt-2 text-sm text-gray-600">
                    Mostrando {alumnosFiltrados.length} de {resumen.totalAlumnos} alumnos
                  </div>
                )}
              </div>

              {/* Tabla de alumnos */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Listado de Alumnos
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                          #
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-8 bg-gray-50 z-10">
                          Legajo
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Alumno
                        </th>
                        {/* Fecha 1 */}
                        <th colSpan={resumen.curso.cantidadTemas || 3} className="px-3 py-3 text-center text-xs font-medium text-blue-600 uppercase tracking-wider bg-blue-50 border-l border-blue-200">
                          Fecha 1
                        </th>
                        {/* Fecha 2 */}
                        <th colSpan={resumen.curso.cantidadTemas || 3} className="px-3 py-3 text-center text-xs font-medium text-purple-600 uppercase tracking-wider bg-purple-50 border-l border-purple-200">
                          Fecha 2
                        </th>
                        {/* Fecha 3 */}
                        <th colSpan={resumen.curso.cantidadTemas || 3} className="px-3 py-3 text-center text-xs font-medium text-orange-600 uppercase tracking-wider bg-orange-50 border-l border-orange-200">
                          Fecha 3
                        </th>
                        {/* Nota Final */}
                        <th colSpan={resumen.curso.cantidadTemas || 3} className="px-3 py-3 text-center text-xs font-medium text-green-600 uppercase tracking-wider bg-green-50 border-l border-green-200">
                          Nota Final
                        </th>
                        <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-300">
                          Condición
                        </th>
                      </tr>
                      {/* Sub-header para temas */}
                      <tr className="bg-gray-100">
                        <th className="sticky left-0 bg-gray-100 z-10"></th>
                        <th className="sticky left-8 bg-gray-100 z-10"></th>
                        <th></th>
                        {/* Temas Fecha 1 */}
                        {Array.from({ length: resumen.curso.cantidadTemas || 3 }).map((_, i) => (
                          <th key={`f1-t${i}`} className="px-2 py-1 text-center text-xs font-medium text-blue-500 bg-blue-50 border-l border-blue-100">
                            T{i + 1}
                          </th>
                        ))}
                        {/* Temas Fecha 2 */}
                        {Array.from({ length: resumen.curso.cantidadTemas || 3 }).map((_, i) => (
                          <th key={`f2-t${i}`} className="px-2 py-1 text-center text-xs font-medium text-purple-500 bg-purple-50 border-l border-purple-100">
                            T{i + 1}
                          </th>
                        ))}
                        {/* Temas Fecha 3 */}
                        {Array.from({ length: resumen.curso.cantidadTemas || 3 }).map((_, i) => (
                          <th key={`f3-t${i}`} className="px-2 py-1 text-center text-xs font-medium text-orange-500 bg-orange-50 border-l border-orange-100">
                            T{i + 1}
                          </th>
                        ))}
                        {/* Temas Nota Final */}
                        {Array.from({ length: resumen.curso.cantidadTemas || 3 }).map((_, i) => (
                          <th key={`final-t${i}`} className="px-2 py-1 text-center text-xs font-medium text-green-500 bg-green-50 border-l border-green-100">
                            T{i + 1}
                          </th>
                        ))}
                        <th className="border-l border-gray-300"></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {alumnosFiltrados.map((alumno, index) => (
                        <tr key={alumno.alumno.id} className="hover:bg-gray-50">
                          <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white z-10">
                            {index + 1}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 sticky left-8 bg-white z-10">
                            {alumno.alumno.numeroAlumno}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                            {alumno.alumno.apellido}, {alumno.alumno.nombre}
                          </td>
                          {/* Notas Fecha 1 */}
                          {alumno.fecha1.notas.map((nota, i) => (
                            <td key={`f1-${i}`} className={`px-2 py-3 whitespace-nowrap text-sm text-center bg-blue-50/30 border-l border-blue-100 ${getNotaColor(nota)}`}>
                              {nota}
                            </td>
                          ))}
                          {/* Notas Fecha 2 */}
                          {alumno.fecha2.notas.map((nota, i) => (
                            <td key={`f2-${i}`} className={`px-2 py-3 whitespace-nowrap text-sm text-center bg-purple-50/30 border-l border-purple-100 ${getNotaColor(nota)}`}>
                              {nota}
                            </td>
                          ))}
                          {/* Notas Fecha 3 */}
                          {alumno.fecha3.notas.map((nota, i) => (
                            <td key={`f3-${i}`} className={`px-2 py-3 whitespace-nowrap text-sm text-center bg-orange-50/30 border-l border-orange-100 ${getNotaColor(nota)}`}>
                              {nota}
                            </td>
                          ))}
                          {/* Nota Final */}
                          {alumno.notaFinal.map((nota, i) => (
                            <td key={`final-${i}`} className={`px-2 py-3 whitespace-nowrap text-sm text-center bg-green-50/30 border-l border-green-100 ${getNotaColor(nota)}`}>
                              {nota}
                            </td>
                          ))}
                          <td className="px-3 py-3 whitespace-nowrap text-center border-l border-gray-300">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getCondicionColor(alumno.condicion)}`}>
                              {alumno.condicion}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {alumnosFiltrados.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    {searchTerm ? 'No se encontraron alumnos con ese criterio de búsqueda.' : 'No hay alumnos en este curso.'}
                  </div>
                )}
              </div>

              {/* Leyenda */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Leyenda de Condiciones</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-start space-x-2">
                    <span className="px-2 py-1 text-xs font-medium rounded-full border bg-green-100 text-green-800 border-green-200">APROBADO</span>
                    <span className="text-gray-600">Aprobó todos los temas (tiene A en cada tema entre las 3 fechas)</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="px-2 py-1 text-xs font-medium rounded-full border bg-red-100 text-red-800 border-red-200">DESAPROBADO</span>
                    <span className="text-gray-600">Se presentó en al menos 2 fechas e intentó rendir todos los temas (D o A)</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="px-2 py-1 text-xs font-medium rounded-full border bg-gray-100 text-gray-800 border-gray-200">AUSENTE</span>
                    <span className="text-gray-600">No cumple con los criterios de aprobado ni desaprobado</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <p className="text-gray-600 text-center">No hay datos disponibles para este curso.</p>
            </div>
          )}
        </>
      )}

      {!selectedCurso && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <p className="text-gray-600 text-center">Selecciona un curso para ver el resumen de notas.</p>
        </div>
      )}
    </div>
  );
};

export default ResumenNotasPage;
